import { IMedia, IPatient } from '@principle-theorem/principle-core/interfaces';
import { Firestore, WithRef, asyncForEach } from '@principle-theorem/shared';
import { Patient } from '../models/patient/patient';

export interface IMediaWithHash {
  media: WithRef<IMedia>;
  md5Hash: string | undefined;
}

export interface IDedupGroup {
  hash: string;
  keep: WithRef<IMedia>;
  duplicates: WithRef<IMedia>[];
}

export function findDuplicateMedia(
  mediaWithHashes: IMediaWithHash[]
): IDedupGroup[] {
  const mediaByHash = new Map<string, WithRef<IMedia>[]>();

  mediaWithHashes.forEach((entry) => {
    if (entry.md5Hash === undefined) {
      return;
    }
    const hash = entry.md5Hash;
    const existingMedia = mediaByHash.get(hash) ?? [];
    existingMedia.push(entry.media);
    mediaByHash.set(hash, existingMedia);
  });

  const dedupGroups: IDedupGroup[] = [];
  mediaByHash.forEach((mediaItems, hash) => {
    if (mediaItems.length <= 1) {
      return;
    }

    const sortedMedia = [...mediaItems].sort((left, right) => {
      const leftHasTags = left.tags?.length > 0 ? 1 : 0;
      const rightHasTags = right.tags?.length > 0 ? 1 : 0;
      if (leftHasTags !== rightHasTags) {
        return rightHasTags - leftHasTags;
      }
      const createdAtDifference =
        left.createdAt.toMillis() - right.createdAt.toMillis();
      if (createdAtDifference !== 0) {
        return createdAtDifference;
      }
      return left.ref.id.localeCompare(right.ref.id);
    });

    const [keep, ...duplicates] = sortedMedia;
    dedupGroups.push({
      hash,
      keep,
      duplicates,
    });
  });

  return dedupGroups;
}

export async function getPatientMediaWithHashes(
  patient: WithRef<IPatient>,
  resolveMd5: (path: string) => Promise<string | undefined>
): Promise<IMediaWithHash[]> {
  const allMedia = await Firestore.getDocs(Patient.mediaCol(patient));
  const activeMedia = allMedia.filter((media) => !media.deleted);

  return asyncForEach(activeMedia, async (media) => {
    try {
      const md5Hash = await resolveMd5(media.path);
      return { media, md5Hash };
    } catch {
      return { media, md5Hash: undefined };
    }
  });
}
